import { InventoryIntent } from '@common/api';
import { EquipSlot } from '@common/data/BodyTypeSlots';
import { EquipmentService } from '@common/services/EquipmentService';
import { InventoryService } from '@common/services/InventoryService';
import { reactive } from 'vue';

import { ClientWorld } from '../ClientWorld';
import { ClientWorldService } from './ClientWorldService';
import { InputBlockingService } from './InputBlockingService';
import { LocalPlayerService } from './LocalPlayerService';

export interface InventorySlotInfo {
  entityId: number;
  textureId: string;
  displayName: string;
  quantity: number;
  maxStack: number;
}

export interface InventoryDragSource {
  type: 'inventory' | 'equipment';
  entityId: number;
  slot: number | EquipSlot;
}

export interface InventoryUIState {
  isOpen: boolean;
  /** The entity whose inventory is being viewed. Null means player's own inventory. */
  targetEntityId: number | null;
  dragSource: InventoryDragSource | null;
  /** The entity being looted (side-by-side view). Null means not looting. */
  lootingTargetId: number | null;
  /** Reactive counter to trigger UI refreshes across all open panels. */
  refreshTick: number;
}

/**
 * Info about an equipment slot for display purposes.
 */
export interface EquipmentSlotInfo {
  slot: EquipSlot;
  itemEntityId: number | null;
  textureId: string | null;
  displayName: string | null;
}

/**
 * Service for managing the Inventory UI state and retrieving inventory data.
 */
export class InventoryUIService {
  public state: InventoryUIState;

  private onMoveRequest:
    | ((
        fromEntityId: number,
        fromSlot: number,
        toEntityId: number,
        toSlot: number,
        intent?: InventoryIntent,
      ) => void)
    | null = null;
  private onDropRequest: ((entityId: number, slotIndex: number) => void) | null = null;
  private onEquipFromInventoryRequest:
    | ((
        inventoryEntityId: number,
        inventorySlot: number,
        equipmentEntityId: number,
        equipSlot: EquipSlot,
        intent?: InventoryIntent,
      ) => void)
    | null = null;
  private onUnequipToInventoryRequest:
    | ((
        equipmentEntityId: number,
        equipSlot: EquipSlot,
        inventoryEntityId: number,
        inventorySlot?: number,
        intent?: InventoryIntent,
      ) => void)
    | null = null;
  private onSwapEquipmentRequest:
    | ((entityId: number, fromSlot: EquipSlot, toSlot: EquipSlot, intent?: InventoryIntent) => void)
    | null = null;
  private onCrossEntitySwapEquipmentRequest:
    | ((
        sourceEntityId: number,
        sourceSlot: EquipSlot,
        targetEntityId: number,
        targetSlot: EquipSlot,
        intent?: InventoryIntent,
      ) => void)
    | null = null;
  private inputBlocking: InputBlockingService | null = null;
  private inventoryService: InventoryService | null = null;
  private equipmentService: EquipmentService | null = null;

  constructor(
    private localPlayer: LocalPlayerService,
    private clientWorld: ClientWorldService,
  ) {
    this.state = reactive({
      isOpen: false,
      targetEntityId: null,
      dragSource: null,
      lootingTargetId: null,
      refreshTick: 0,
    });
  }

  /**
   * Set the inventory service for ECS-level operations.
   * Called during service container initialization.
   */
  public setInventoryService(service: InventoryService): void {
    this.inventoryService = service;
  }

  /**
   * Set the equipment service for equipment operations.
   * Called during service container initialization.
   */
  public setEquipmentService(service: EquipmentService): void {
    this.equipmentService = service;
  }

  /**
   * Register a callback to handle move requests.
   * This decouples the service from network layer - the callback is wired up in main.ts
   * to send WebSocket messages.
   */
  public setMoveCallback(
    callback: (
      fromEntityId: number,
      fromSlot: number,
      toEntityId: number,
      toSlot: number,
      intent?: InventoryIntent,
    ) => void,
  ): void {
    this.onMoveRequest = callback;
  }

  /**
   * Request moving an item from one slot to another via the network layer.
   *
   * CRITICAL: This method performs translation from client-side entity IDs to
   * server-authoritative entity IDs using the NetworkIdentity component.
   */
  public requestMove(
    fromEntityId: number,
    fromSlot: number,
    toEntityId: number,
    toSlot: number,
    intent?: InventoryIntent,
  ): void {
    if (this.onMoveRequest) {
      const fromServerId = this.getServerEntityId(fromEntityId);
      const toServerId = this.getServerEntityId(toEntityId);
      if (fromServerId !== undefined && toServerId !== undefined) {
        this.onMoveRequest(fromServerId, fromSlot, toServerId, toSlot, intent);
      } else {
        console.warn(
          `[InventoryUIService] Could not resolve server ID for move: from=${fromEntityId}, to=${toEntityId}`,
        );
      }
    }
  }

  public getWorld(): ClientWorld {
    return this.clientWorld.getWorld();
  }

  /**
   * Register a callback to handle drop requests.
   * This decouples the service from network layer - the callback is wired up in main.ts
   * to send WebSocket messages.
   */
  public setDropCallback(callback: (entityId: number, slotIndex: number) => void): void {
    this.onDropRequest = callback;
  }

  /**
   * Request dropping an item from inventory to the world.
   * Invokes the registered callback to send the request to the server.
   */
  public requestDrop(entityId: number, slotIndex: number): void {
    if (this.onDropRequest) {
      const serverEntityId = this.getServerEntityId(entityId);
      if (serverEntityId !== undefined) {
        this.onDropRequest(serverEntityId, slotIndex);
      } else {
        console.warn(
          `[InventoryUIService] Could not resolve server ID for drop: entityId=${entityId}`,
        );
      }
    }
  }

  // ============ Equipment Callbacks ============

  public setEquipFromInventoryCallback(
    callback: (
      inventoryEntityId: number,
      inventorySlot: number,
      equipmentEntityId: number,
      equipSlot: EquipSlot,
      intent?: InventoryIntent,
    ) => void,
  ): void {
    this.onEquipFromInventoryRequest = callback;
  }
  public setUnequipToInventoryCallback(
    callback: (
      equipmentEntityId: number,
      equipSlot: EquipSlot,
      inventoryEntityId: number,
      inventorySlot?: number,
      intent?: InventoryIntent,
    ) => void,
  ): void {
    this.onUnequipToInventoryRequest = callback;
  }
  public setSwapEquipmentCallback(
    callback: (
      entityId: number,
      fromSlot: EquipSlot,
      toSlot: EquipSlot,
      intent?: InventoryIntent,
    ) => void,
  ): void {
    this.onSwapEquipmentRequest = callback;
  }
  public setCrossEntitySwapEquipmentCallback(
    callback: (
      sourceEntityId: number,
      sourceSlot: EquipSlot,
      targetEntityId: number,
      targetSlot: EquipSlot,
      intent?: InventoryIntent,
    ) => void,
  ): void {
    this.onCrossEntitySwapEquipmentRequest = callback;
  }

  private requestEquipFromInventory(
    inventoryEntityId: number,
    inventorySlot: number,
    equipmentEntityId: number,
    equipSlot: EquipSlot,
    intent?: InventoryIntent,
  ): void {
    if (this.onEquipFromInventoryRequest) {
      const serverInvId = this.getServerEntityId(inventoryEntityId);
      const serverEquipId = this.getServerEntityId(equipmentEntityId);
      if (serverInvId !== undefined && serverEquipId !== undefined) {
        this.onEquipFromInventoryRequest(
          serverInvId,
          inventorySlot,
          serverEquipId,
          equipSlot,
          intent,
        );
      } else {
        console.warn(
          `[InventoryUIService] Could not resolve server IDs for equip: inv=${inventoryEntityId}, equip=${equipmentEntityId}`,
        );
      }
    }
  }

  private requestUnequipToInventory(
    equipmentEntityId: number,
    equipSlot: EquipSlot,
    inventoryEntityId: number,
    inventorySlot?: number,
    intent?: InventoryIntent,
  ): void {
    if (this.onUnequipToInventoryRequest) {
      const serverEquipId = this.getServerEntityId(equipmentEntityId);
      const serverInvId = this.getServerEntityId(inventoryEntityId);
      if (serverEquipId !== undefined && serverInvId !== undefined) {
        this.onUnequipToInventoryRequest(
          serverEquipId,
          equipSlot,
          serverInvId,
          inventorySlot,
          intent,
        );
      } else {
        console.warn(
          `[InventoryUIService] Could not resolve server IDs for unequip: equip=${equipmentEntityId}, inv=${inventoryEntityId}`,
        );
      }
    }
  }

  private requestSwapEquipment(
    entityId: number,
    fromSlot: EquipSlot,
    toSlot: EquipSlot,
    intent?: InventoryIntent,
  ): void {
    if (this.onSwapEquipmentRequest) {
      const serverEntityId = this.getServerEntityId(entityId);
      if (serverEntityId !== undefined) {
        this.onSwapEquipmentRequest(serverEntityId, fromSlot, toSlot, intent);
      } else {
        console.warn(
          `[InventoryUIService] Could not resolve server ID for swap: entityId=${entityId}`,
        );
      }
    }
  }

  private requestCrossEntitySwapEquipment(
    sourceEntityId: number,
    sourceSlot: EquipSlot,
    targetEntityId: number,
    targetSlot: EquipSlot,
    intent?: InventoryIntent,
  ): void {
    if (this.onCrossEntitySwapEquipmentRequest) {
      const serverSourceId = this.getServerEntityId(sourceEntityId);
      const serverTargetId = this.getServerEntityId(targetEntityId);
      if (serverSourceId !== undefined && serverTargetId !== undefined) {
        this.onCrossEntitySwapEquipmentRequest(
          serverSourceId,
          sourceSlot,
          serverTargetId,
          targetSlot,
          intent,
        );
      } else {
        console.warn(
          `[InventoryUIService] Could not resolve server IDs for cross-entity swap: source=${sourceEntityId}, target=${targetEntityId}`,
        );
      }
    }
  }

  /**
   * Translates a client-side entity ID to its server-side equivalent.
   *
   * This is necessary because the client ECS (ClientWorld) uses its own local
   * entity IDs, while the server uses its own authoritative IDs. The mapping
   * is maintained via the NetworkIdentity component on each networked entity.
   *
   * @param clientEntityId - The ID of the entity in the client's local ECS world.
   * @returns The corresponding authoritative server ID, or undefined if not found.
   */
  private getServerEntityId(clientEntityId: number): number | undefined {
    // Optimization: if it's the local player, we already have it in LocalPlayerService
    if (clientEntityId === this.localPlayer.entityId) {
      return this.localPlayer.serverEntityId;
    }

    const world = this.clientWorld.getWorld();
    const networkIdentity = world.getComponent(clientEntityId, 'NetworkIdentity');
    return networkIdentity?.serverID;
  }

  /**
   * Get the local player's entity ID for inventory operations.
   */
  public getPlayerEntityId(): number | undefined {
    return this.localPlayer.entityId;
  }

  /**
   * Perform an optimistic move operation on the client ECS world,
   * then send the request to the server.
   *
   * This ensures the client ECS state matches what we expect the server
   * to confirm, reducing sync issues.
   */
  public performMove(
    fromEntityId: number,
    fromSlot: number,
    toEntityId: number,
    toSlot: number,
    intent?: InventoryIntent,
  ): boolean {
    if (!this.inventoryService) {
      return false;
    }

    // Optimistic ECS mutation
    const success = this.inventoryService.moveItem(fromEntityId, fromSlot, toEntityId, toSlot);

    // Send to server regardless (server is authoritative)
    this.requestMove(fromEntityId, fromSlot, toEntityId, toSlot, intent);

    // Trigger UI refresh across all panels for immediate feedback
    this.notifyRefresh();

    return success;
  }

  /**
   * Perform an optimistic drop operation on the client ECS world,
   * then send the request to the server.
   *
   * Note: We only clear the slot; the full drop logic (adding world
   * components, throw animation) happens server-side.
   */
  public performDrop(entityId: number, slotIndex: number): boolean {
    if (!this.inventoryService) {
      return false;
    }

    // Optimistic ECS mutation - just clear the slot
    const itemEntityId = this.inventoryService.removeItem(entityId, slotIndex);

    // Send to server regardless (server is authoritative)
    this.requestDrop(entityId, slotIndex);

    // Trigger UI refresh across all panels
    this.notifyRefresh();

    return itemEntityId !== null;
  }

  // ============ Equipment Operations with Optimistic Updates ============

  /**
   * Perform equip from inventory with optimistic update.
   * Supports cross-entity operations (e.g., player inventory → corpse equipment).
   */
  public performEquipFromInventory(
    inventoryEntityId: number,
    inventorySlot: number,
    equipmentEntityId: number,
    equipSlot: EquipSlot,
    intent?: InventoryIntent,
  ): boolean {
    if (!this.inventoryService) {
      return false;
    }

    const world = this.clientWorld.getWorld();
    const inventory = world.getComponent(inventoryEntityId, 'Inventory');
    if (!inventory) return false;

    const itemEntityId = inventory.slots[inventorySlot];
    if (itemEntityId === null || itemEntityId === undefined) return false;

    // [MODIFIED] Validate compatibility before optimistic update
    if (!this.canEquipToSlot(equipmentEntityId, itemEntityId, equipSlot)) {
      console.warn(
        `[InventoryUIService] Equip validation failed: item ${itemEntityId} cannot fit in slot ${equipSlot} on entity ${equipmentEntityId}`,
      );
      return false;
    }

    const equipment = world.getComponent(equipmentEntityId, 'Equipment');
    if (!equipment) return false;

    // Get existing item in equipment slot (if any)
    const existingItemId = equipment.slots[equipSlot] ?? null;

    // Remove item from inventory
    const removedItem = this.inventoryService.removeItem(inventoryEntityId, inventorySlot);
    if (removedItem === null) return false;

    // If there's an existing item in the slot, put it in the inventory slot we just freed
    if (existingItemId !== null) {
      const addResult = this.inventoryService.addItem(
        inventoryEntityId,
        existingItemId,
        inventorySlot,
      );
      if (addResult === null) {
        // Rollback: put the original item back
        const rollbackResult = this.inventoryService.addItem(
          inventoryEntityId,
          removedItem,
          inventorySlot,
        );
        if (rollbackResult === null) {
          console.error(
            'InventoryUIService: CRITICAL: Failed to rollback inventory change after addItem failure',
            { inventoryEntityId, inventorySlot, removedItem },
          );
        }
        return false;
      }
    }

    // Put the new item in the equipment slot
    equipment.slots[equipSlot] = removedItem;
    world.updateComponent(equipmentEntityId, equipment, { slots: equipment.slots });

    // Send to server
    this.requestEquipFromInventory(
      inventoryEntityId,
      inventorySlot,
      equipmentEntityId,
      equipSlot,
      intent,
    );

    // Trigger UI refresh
    this.notifyRefresh();

    return true;
  }

  /**
   * Perform unequip to inventory with optimistic update.
   * Supports cross-entity operations (e.g., corpse equipment → player inventory).
   */
  public performUnequipToInventory(
    equipmentEntityId: number,
    equipSlot: EquipSlot,
    inventoryEntityId: number,
    inventorySlot?: number,
    intent?: InventoryIntent,
  ): boolean {
    if (!this.inventoryService) {
      return false;
    }

    const world = this.clientWorld.getWorld();
    const equipment = world.getComponent(equipmentEntityId, 'Equipment');
    if (!equipment) return false;

    const itemEntityId = equipment.slots[equipSlot];
    if (itemEntityId === null || itemEntityId === undefined) return false;

    // Try to add item to target inventory
    const addResult = this.inventoryService.addItem(inventoryEntityId, itemEntityId, inventorySlot);
    if (addResult === null) return false;

    // Remove from equipment slot
    equipment.slots[equipSlot] = null;
    world.updateComponent(equipmentEntityId, equipment, { slots: equipment.slots });

    // Send to server
    this.requestUnequipToInventory(
      equipmentEntityId,
      equipSlot,
      inventoryEntityId,
      inventorySlot,
      intent,
    );

    // Trigger UI refresh
    this.notifyRefresh();

    return true;
  }

  /**
   * Perform swap between equipment slots with optimistic update.
   */
  public performSwapEquipment(
    entityId: number,
    fromSlot: EquipSlot,
    toSlot: EquipSlot,
    intent?: InventoryIntent,
  ): boolean {
    const world = this.clientWorld.getWorld();
    const equipment = world.getComponent(entityId, 'Equipment');
    if (!equipment) return false;

    const fromItem = equipment.slots[fromSlot] ?? null;
    if (fromItem === null) return false;

    // Optimistic swap
    const toItem = equipment.slots[toSlot] ?? null;
    equipment.slots[fromSlot] = toItem;
    equipment.slots[toSlot] = fromItem;

    // Notify ECS about the updated Equipment component so listeners receive the swap.
    // Pass the component instance as second argument, and the updated data as third.
    world.updateComponent(entityId, equipment, { slots: equipment.slots });

    // Send to server
    this.requestSwapEquipment(entityId, fromSlot, toSlot, intent);

    // Trigger UI refresh
    this.notifyRefresh();

    return true;
  }

  /**
   * Perform swap between equipment slots on different entities with optimistic update.
   * Used for cross-entity looting (e.g., player helmet ↔ corpse helmet).
   */
  public performCrossEntitySwapEquipment(
    sourceEntityId: number,
    sourceSlot: EquipSlot,
    targetEntityId: number,
    targetSlot: EquipSlot,
    intent?: InventoryIntent,
  ): boolean {
    const world = this.clientWorld.getWorld();

    const sourceEquipment = world.getComponent(sourceEntityId, 'Equipment');
    const targetEquipment = world.getComponent(targetEntityId, 'Equipment');
    if (!sourceEquipment || !targetEquipment) return false;

    // At least one slot should have an item
    const sourceItem = sourceEquipment.slots[sourceSlot] ?? null;
    const targetItem = targetEquipment.slots[targetSlot] ?? null;
    if (sourceItem === null && targetItem === null) return false;

    // [MODIFIED] Validate compatibility for both items in their new slots
    if (sourceItem !== null && !this.canEquipToSlot(targetEntityId, sourceItem, targetSlot)) {
      console.warn(
        `[InventoryUIService] Swap validation failed: item ${sourceItem} cannot fit in slot ${targetSlot} on target entity ${targetEntityId}`,
      );
      return false;
    }

    if (targetItem !== null && !this.canEquipToSlot(sourceEntityId, targetItem, sourceSlot)) {
      console.warn(
        `[InventoryUIService] Swap validation failed: item ${targetItem} cannot fit in slot ${sourceSlot} on source entity ${sourceEntityId}`,
      );
      return false;
    }

    // Optimistic swap
    sourceEquipment.slots[sourceSlot] = targetItem;
    targetEquipment.slots[targetSlot] = sourceItem;

    // Update both equipment components
    world.updateComponent(sourceEntityId, sourceEquipment, { slots: sourceEquipment.slots });
    world.updateComponent(targetEntityId, targetEquipment, { slots: targetEquipment.slots });

    // Send to server
    this.requestCrossEntitySwapEquipment(
      sourceEntityId,
      sourceSlot,
      targetEntityId,
      targetSlot,
      intent,
    );

    // Trigger UI refresh
    this.notifyRefresh();

    return true;
  }

  /**
   * Check if an item can be equipped to a specific slot.
   * Used for drag-and-drop validation UI feedback.
   */
  public canEquipToSlot(entityId: number, itemEntityId: number, equipSlot: EquipSlot): boolean {
    if (!this.equipmentService) {
      return false;
    }

    // Check if slot is valid for body type
    const validSlots = this.equipmentService.getValidSlots(entityId);
    if (!validSlots.includes(equipSlot)) {
      return false;
    }

    // Hand slots accept any item
    if (this.equipmentService.isHandSlot(equipSlot)) {
      return true;
    }

    // Non-hand slots require Equippable component with matching validSlots
    const world = this.clientWorld.getWorld();
    const equippable = world.getComponent(itemEntityId, 'Equippable');
    return equippable !== undefined && equippable.validSlots.includes(equipSlot);
  }

  /**
   * Get the valid equipment slots for the player's body type.
   */
  public getValidEquipSlots(entityId: number): EquipSlot[] {
    if (!this.equipmentService) {
      return [];
    }
    return this.equipmentService.getValidSlots(entityId);
  }

  /**
   * Set the input blocking service for blocking movement when inventory is open.
   */
  public setInputBlocking(service: InputBlockingService): void {
    this.inputBlocking = service;
  }

  /**
   * Set the drag source for inventory items.
   */
  public setDragSource(source: InventoryDragSource | null): void {
    this.state.dragSource = source;
  }

  public toggle(): void {
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  public open(): void {
    // Don't allow opening if something else (e.g., dialogue) is already blocking input
    if (this.inputBlocking?.isMovementBlocked()) {
      return;
    }
    this.inputBlocking?.block('inventory');
    this.state.targetEntityId = null; // Default to player's own inventory
    this.state.isOpen = true;
  }

  /**
   * Open the inventory UI for a specific entity.
   * Used when clicking on entities to view their inventory.
   */
  public openForEntity(entityId: number): void {
    // Don't allow opening if something else is already blocking input
    if (this.inputBlocking?.isMovementBlocked()) {
      return;
    }
    this.inputBlocking?.block('inventory');
    this.state.targetEntityId = entityId;
    this.state.isOpen = true;
  }

  /**
   * Open the looting UI for a specific entity.
   */
  public openLooting(entityId: number): void {
    if (this.inputBlocking?.isMovementBlocked()) {
      return;
    }
    this.inputBlocking?.block('inventory');
    this.state.lootingTargetId = entityId;
    this.state.isOpen = true;
  }

  public close(): void {
    this.inputBlocking?.unblock('inventory');
    this.state.targetEntityId = null;
    this.state.lootingTargetId = null;
    this.state.isOpen = false;
  }

  /**
   * Get the entity ID whose inventory should be displayed.
   * Falls back to the local player if no target is set.
   */
  public getTargetEntityId(): number | undefined {
    return this.state.targetEntityId ?? this.localPlayer.entityId;
  }

  /**
   * Returns true if the inventory is in read-only mode.
   * This is the case when viewing another entity's inventory (not the player's),
   * EXCEPT when looting.
   */
  public isReadOnly(): boolean {
    // If looting, we are never read-only (we can take items)
    if (this.state.lootingTargetId !== null) {
      return false;
    }

    const targetId = this.state.targetEntityId;
    if (targetId === null) {
      return false;
    }
    // Read-only only when inspecting an entity other than the local player
    return targetId !== this.localPlayer.entityId;
  }

  /**
   * Returns true if the looting UI is currently open.
   */
  public isLooting(): boolean {
    return this.state.lootingTargetId !== null;
  }

  /**
   * Notify the UI to refresh inventory display.
   * Increments the reactive refreshTick which is watched by all active panels.
   */
  public notifyRefresh(): void {
    this.state.refreshTick++;
  }

  /**
   * Retrieves the current inventory slots for the local player.
   * Returns an array where each index corresponds to a slot.
   * Null indicates an empty slot.
   */
  public getSlots(entityId: number): (InventorySlotInfo | null)[] {
    const world = this.clientWorld.getWorld();
    const inventory = world.getComponent(entityId, 'Inventory');
    if (!inventory) {
      return [];
    }

    return inventory.slots.map((itemId) => {
      if (itemId === null) {
        return null;
      }

      // Verify the item entity exists in the client world
      if (!world.getEntity(itemId)) {
        return null;
      }

      const displayName = world.getComponent(itemId, 'DisplayName');
      const sprite = world.getComponent(itemId, 'Sprite');

      let textureId = '';
      if (sprite) {
        textureId = sprite.textureId;
      } else {
        // Fallback: Check if it's a spritesheet sprite pending resolution
        // (common for newly dropped/picked up items)
        const spritesheetSprite = world.getComponent(itemId, 'SpritesheetSprite');
        if (spritesheetSprite) {
          // Construct texture ID manually: {id}_{frame}
          textureId = `${spritesheetSprite.spritesheetId}_${spritesheetSprite.frameIndex}`;
        } else {
          console.warn(`Inventory item ${itemId} missing Sprite component`);
          return null;
        }
      }

      const stack = world.getComponent(itemId, 'Stack');

      return {
        entityId: itemId,
        textureId,
        displayName: displayName ? displayName.name : 'Unknown Item',
        quantity: stack ? stack.quantity : 1,
        maxStack: stack ? stack.maxStack : 1,
      };
    });
  }

  public getMaxSlots(entityId: number): number {
    const world = this.clientWorld.getWorld();
    const inventory = world.getComponent(entityId, 'Inventory');
    return inventory ? inventory.maxSlots : 10;
  }

  // ============ Equipment Slot Data ============

  /**
   * Retrieves the current equipment slots for the local player.
   * Returns info about each valid equipment slot and what's equipped.
   */
  public getEquipmentSlots(entityId: number): EquipmentSlotInfo[] {
    if (!this.equipmentService) {
      return [];
    }

    const world = this.clientWorld.getWorld();

    const validSlots = this.getValidEquipSlots(entityId);
    const equipment = world.getComponent(entityId, 'Equipment');

    if (!equipment) {
      // Return empty info for valid slots if no equipment component yet
      return validSlots.map((slot) => ({
        slot,
        itemEntityId: null,
        textureId: null,
        displayName: null,
      }));
    }

    return validSlots.map((slot): EquipmentSlotInfo => {
      const itemEntityId = equipment?.slots[slot] ?? null;

      if (itemEntityId === null || !world.getEntity(itemEntityId)) {
        return {
          slot,
          itemEntityId: null,
          textureId: null,
          displayName: null,
        };
      }

      const displayName = world.getComponent(itemEntityId, 'DisplayName');
      const sprite = world.getComponent(itemEntityId, 'Sprite');

      let textureId: string | null = null;
      if (sprite) {
        textureId = sprite.textureId;
      } else {
        const spritesheetSprite = world.getComponent(itemEntityId, 'SpritesheetSprite');
        if (spritesheetSprite) {
          textureId = `${spritesheetSprite.spritesheetId}_${spritesheetSprite.frameIndex}`;
        }
      }

      return {
        slot,
        itemEntityId,
        textureId,
        displayName: displayName ? displayName.name : 'Unknown Item',
      };
    });
  }
}
